import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import {
  EmailAddressesClient,
  EMAIL_PAGE_SIZE,
  usedSinceFor,
  type EmailAddressesSearch,
} from "@/features/parties/EmailAddressesClient";
import { listEmailAddresses } from "@/features/parties/emailAddresses.api";
import { applyParamUpdates } from "@/lib/searchParams";
import { fetchSession, sessionQueryKey } from "@/lib/auth";
import { mayAccess } from "@/lib/access";

// Behind the right `email_addresses` (Beheer › Rechten).
// The session is already cached by the _shell layout guard, so this resolves
// instantly; without it, back to the dashboard. An affordance/UX gate —
// the API enforces the same right regardless.
export const Route = createFileRoute("/_shell/email-addresses")({
  beforeLoad: async ({ context }) => {
    const session = await context.queryClient.ensureQueryData({
      queryKey: sessionQueryKey,
      queryFn: fetchSession,
    });
    if (!mayAccess(session, ["email_addresses"])) {
      throw redirect({ to: "/dashboard" });
    }
  },
  validateSearch: (search: Record<string, unknown>): EmailAddressesSearch => ({
    q: typeof search.q === "string" && search.q ? search.q : undefined,
    status: search.status === "active" || search.status === "inactive" ? search.status : undefined,
    since:
      search.since === "month" || search.since === "prevMonth" || search.since === "90d" ? search.since : undefined,
    page: typeof search.page === "string" ? Number(search.page) || undefined : undefined,
  }),
  component: EmailAddressesPage,
});

function EmailAddressesPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();

  const query = useQuery({
    queryKey: ["email-addresses", search],
    queryFn: () =>
      listEmailAddresses({
        q: search.q,
        isActive: search.status === "active" ? "true" : search.status === "inactive" ? "false" : undefined,
        usedSince: usedSinceFor(search.since),
        page: search.page,
        pageSize: EMAIL_PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });

  function updateParams(updates: Record<string, string>) {
    void navigate({
      to: "/email-addresses",
      search: (prev) => applyParamUpdates(prev as Record<string, string | undefined>, updates),
    });
  }

  return (
    <EmailAddressesClient
      result={query.data}
      isLoading={query.isLoading}
      search={search}
      updateParams={updateParams}
    />
  );
}
